// src/app/api/adminApi.ts
//
// RTK Query endpoints for the admin dashboard. Every endpoint maps to the
// server's /api/admin/* routes (all gated by verifyAdmin on the backend).
import { baseApi } from "./baseApi";

/* ─── types ───────────────────────────────────────────────────────────────── */
export interface AdminStats {
  leads: {
    total: number;
    byType: {
      lead: number;
      custom_funnel: number;
      faith_that_works: number;
      monetize_knowledge: number;
      wealth_method: number;
    };
  };
  users: { total: number; admins: number };
  orders: { total: number; paid: number; byStatus: Record<string, number> };
  revenue: { paid: number; currency: string };
  followUps: { pending: number; overdue: number };
  recentOrders: AdminOrder[];
}

export interface RevenuePoint {
  date: string;
  revenue: number;
  orders: number;
  leads: number;
}

export interface AdminOrder {
  _id: string;
  name?: string;
  email?: string;
  amount?: number;
  currency?: string;
  status: "pending" | "paid" | "failed" | "cancelled";
  paystackReference?: string;
  fulfilled?: boolean;
  fulfilledAt?: string;
  paidAt?: string;
  metadata?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: "admin" | "user";
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt?: string;
}

export interface FollowUp {
  _id: string;
  contactEmail: string;
  contactName?: string;
  type: "note" | "call" | "email" | "task" | "status_change" | "sms";
  message?: string;
  subject?: string;
  channel?: string;
  dueAt?: string | null;
  completed?: boolean;
  completedAt?: string | null;
  fromStatus?: string;
  toStatus?: string;
  createdByName?: string;
  createdAt?: string;
}

export interface TimelineEntry {
  kind: "followup" | "order" | "event";
  subKind?: string;
  at: string;
  title: string;
  message?: string;
  by?: string;
  dueAt?: string | null;
  completed?: boolean;
  id: string;
}

export interface TagItem {
  _id: string;
  name: string;
  color?: string;
  description?: string;
}

interface Paginated<T> {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  items: T[];
}

/* ─── api ─────────────────────────────────────────────────────────────────── */
export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /* stats + charts */
    getAdminStats: builder.query<{ success: boolean; data: AdminStats }, void>({
      query: () => "/admin/stats",
      providesTags: [{ type: "AdminStats", id: "SUMMARY" }],
    }),
    getRevenueSeries: builder.query<
      { success: boolean; data: RevenuePoint[] },
      { days?: number }
    >({
      query: ({ days = 30 } = {}) => `/admin/revenue-series?days=${days}`,
      providesTags: [{ type: "AdminStats", id: "SERIES" }],
    }),

    /* orders / payments */
    getAdminOrders: builder.query<
      {
        success: boolean;
        data: {
          orders: AdminOrder[];
          pagination: Omit<Paginated<AdminOrder>, "items">;
        };
      },
      { status?: string; q?: string; page?: number; limit?: number }
    >({
      query: (params) => ({ url: "/admin/orders", params }),
      providesTags: [{ type: "AdminOrder", id: "LIST" }],
    }),
    updateAdminOrder: builder.mutation<
      { success: boolean; data: AdminOrder },
      { id: string; status?: string; fulfilled?: boolean; note?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/orders/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "AdminOrder", id: "LIST" },
        { type: "AdminStats", id: "SUMMARY" },
      ],
    }),

    /* users */
    getAdminUsers: builder.query<
      {
        success: boolean;
        data: {
          users: AdminUser[];
          pagination: Omit<Paginated<AdminUser>, "items">;
        };
      },
      { role?: string; q?: string; page?: number; limit?: number }
    >({
      query: (params) => ({ url: "/admin/users", params }),
      providesTags: [{ type: "AdminUser", id: "LIST" }],
    }),
    createAdminUser: builder.mutation<
      { success: boolean; data: AdminUser },
      {
        email: string;
        password: string;
        firstName: string;
        lastName: string;
        role: "admin" | "user";
      }
    >({
      query: (body) => ({ url: "/admin/users", method: "POST", body }),
      invalidatesTags: [
        { type: "AdminUser", id: "LIST" },
        { type: "AdminStats", id: "SUMMARY" },
      ],
    }),
    updateAdminUser: builder.mutation<
      { success: boolean; data: AdminUser },
      { id: string; role?: "admin" | "user"; isEmailVerified?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/users/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "AdminUser", id: "LIST" }],
    }),
    deleteAdminUser: builder.mutation<
      { success: boolean },
      { id: string }
    >({
      query: ({ id }) => ({ url: `/admin/users/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "AdminUser", id: "LIST" },
        { type: "AdminStats", id: "SUMMARY" },
      ],
    }),

    /* follow-ups */
    getFollowUps: builder.query<
      {
        success: boolean;
        data: {
          followups: FollowUp[];
          pagination: Omit<Paginated<FollowUp>, "items">;
        };
      },
      { email?: string; type?: string; status?: string; page?: number; limit?: number }
    >({
      query: (params) => ({ url: "/admin/followups", params }),
      providesTags: [{ type: "FollowUp", id: "LIST" }],
    }),
    createFollowUp: builder.mutation<
      { success: boolean; data: FollowUp },
      {
        contactEmail: string;
        contactName?: string;
        type?: string;
        message?: string;
        channel?: string;
        dueAt?: string | null;
      }
    >({
      query: (body) => ({ url: "/admin/followups", method: "POST", body }),
      invalidatesTags: [
        { type: "FollowUp", id: "LIST" },
        { type: "Timeline", id: "LIST" },
        { type: "AdminStats", id: "SUMMARY" },
      ],
    }),
    updateFollowUp: builder.mutation<
      { success: boolean; data: FollowUp },
      { id: string; message?: string; completed?: boolean; dueAt?: string | null }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/followups/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "FollowUp", id: "LIST" },
        { type: "Timeline", id: "LIST" },
        { type: "AdminStats", id: "SUMMARY" },
      ],
    }),
    deleteFollowUp: builder.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({ url: `/admin/followups/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "FollowUp", id: "LIST" },
        { type: "Timeline", id: "LIST" },
      ],
    }),

    /* per-contact */
    getContactTimeline: builder.query<
      { success: boolean; data: { email: string; lead: any; timeline: TimelineEntry[] } },
      { email: string }
    >({
      query: ({ email }) =>
        `/admin/contacts/${encodeURIComponent(email)}/timeline`,
      providesTags: [{ type: "Timeline", id: "LIST" }],
    }),
    sendContactEmail: builder.mutation<
      { success: boolean; data: FollowUp },
      { email: string; subject: string; message: string; contactName?: string }
    >({
      query: ({ email, ...body }) => ({
        url: `/admin/contacts/${encodeURIComponent(email)}/email`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Timeline", id: "LIST" },
        { type: "FollowUp", id: "LIST" },
      ],
    }),
    sendBulkEmail: builder.mutation<
      {
        success: boolean;
        data: {
          requested: number;
          sent: number;
          failed: number;
          failures: Array<{ email: string; error: string }>;
        };
      },
      {
        recipients: Array<{ email: string; name?: string }>;
        subject: string;
        message: string;
      }
    >({
      query: (body) => ({
        url: "/admin/contacts/bulk-email",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Timeline", id: "LIST" },
        { type: "FollowUp", id: "LIST" },
      ],
    }),
    updateLeadStatus: builder.mutation<
      { success: boolean; data: any },
      { email: string; status: string }
    >({
      query: (body) => ({
        url: "/admin/leads/status",
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "Timeline", id: "LIST" },
        { type: "Contacts", id: "LIST" },
        { type: "FollowUp", id: "LIST" },
      ],
    }),

    /* tags */
    getTags: builder.query<{ success: boolean; data: TagItem[] }, void>({
      query: () => "/admin/tags",
      providesTags: [{ type: "Tag", id: "LIST" }],
    }),
    createTag: builder.mutation<
      { success: boolean; data: TagItem },
      { name: string; color?: string; description?: string }
    >({
      query: (body) => ({ url: "/admin/tags", method: "POST", body }),
      invalidatesTags: [{ type: "Tag", id: "LIST" }],
    }),
  }),
});

export const {
  useGetAdminStatsQuery,
  useGetRevenueSeriesQuery,
  useGetAdminOrdersQuery,
  useUpdateAdminOrderMutation,
  useGetAdminUsersQuery,
  useCreateAdminUserMutation,
  useUpdateAdminUserMutation,
  useDeleteAdminUserMutation,
  useGetFollowUpsQuery,
  useCreateFollowUpMutation,
  useUpdateFollowUpMutation,
  useDeleteFollowUpMutation,
  useGetContactTimelineQuery,
  useSendContactEmailMutation,
  useSendBulkEmailMutation,
  useUpdateLeadStatusMutation,
  useGetTagsQuery,
  useCreateTagMutation,
} = adminApi;
